import {
  ApplicationInitStatus,
  ApplicationRef,
  inject,
  Injector,
  Provider,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { provideRouter, withDisabledInitialNavigation } from '@angular/router';

import moment from 'moment-timezone';
import { lastValueFrom, Subject } from 'rxjs';

import { APP_CONFIG } from '@core/common-legacy';
import { I18nService, patchMoment } from '@core/i18n-legacy';
import {
  authorize,
  handleAuthError,
  redirectToOneDomainIfLegacyDomainVisited,
  TellentClientAuthError,
} from '@core/tellent-client-logic';
import { injectDevFlagPromise } from '@core/tellent-dev-flags-logic';
import { TellentGettingStartedFacade } from '@core/tellent-getting-started-data';
import {
  TellentMainNavigationStateService,
  TellentNavigationDataFacade,
  TellentNavigationRoutingService,
} from '@core/tellent-logic';
import { injectIsNativeMobileHr, setupNativeMobileHr } from '@core/tellent-mobile-contracts';
import { currentUserProfileTellentClientRoute } from '@core/tellent-smart-ui';
import {
  AppKind,
  injectTellentAppKind,
  injectTellentClientInternalRoutes,
  ORG_GUID,
  provideTellentClientInternalRoute,
  TELLENT_ADOPTED_STYLESHEETS,
  TELLENT_CLIENT_CONFIG,
  TELLENT_INITIAL_CURRENT_USER,
  TellentAdminAppConfig,
  TellentClientBootstrapData,
  TellentClientBootstrapDataV2,
  TellentClientConfig,
  TellentClientInitData,
  TellentCurrentUser,
  TellentDevFlag,
  TellentRoute,
} from '@core/tellent-types';
import { injectTellentAppUrlBuilder } from '@core/tellent-util';
import { defineCustomElements } from '@tellent/tellent-util';

import {
  bootstrapInjectorProviders,
  provideDynamicAnalyticsClient,
  resolveAppProviders,
} from './app.config';
import { environments } from './app.environments';
import { AppRevisionToken } from './app.revision';
import { ROUTES } from './app.routes';
import { TellentClientBootstrap } from './bootstrap';
import { ReportsFacade } from './reports-facade';
import { MobilePushDeviceRegistrationService } from './services/mobile-push-device-registration.service';
import { TellentClientGlobalNotificationsService } from './services/tellent-client-global-notifications.service';
import { TellentClientComponents } from './tellent-client-components';
import { resolveErrorHandlerProviders } from './tellent-client-error-handling';

import 'moment/locale/de';
import 'moment/locale/nl';
import 'moment/locale/fr';
import 'moment/locale/fi';
import 'moment/locale/sv';

patchMoment(moment);
moment.locale('en');

export class TellentClient {
  #ready$ = new Subject<ApplicationRef>();

  #injector: Injector;
  #config: TellentClientConfig;
  #bootstrapProviders: Provider[];
  #dynamicConfig = {
    customStylesheets: signal<CSSStyleSheet[]>([]),
    analyticsCallbacks: signal<TellentClientConfig['analyticsCallbacks'] | null>(null),
  };

  public readonly ready = lastValueFrom(this.#ready$);

  public internalRoutes: TellentRoute[] = [];

  constructor(config: TellentClientConfig) {
    this.#config = this.validateConfig(config);
    this.#bootstrapProviders = bootstrapInjectorProviders(this.#config);
    this.#dynamicConfig.analyticsCallbacks.set(this.#config.analyticsCallbacks ?? null);
    this.#injector = Injector.create({
      name: 'Tellent client bootstrap injector',
      providers: [this.#bootstrapProviders],
    });
  }

  public async initWithSelfAuth(
    appKind: `${AppKind}`,
  ): Promise<Partial<TellentClientBootstrapDataV2> | null | void> {
    return runInInjectionContext(this.#injector, async () => {
      const config = inject<TellentAdminAppConfig>(APP_CONFIG);
      const isNativeMobileHr = injectIsNativeMobileHr();
      if (isNativeMobileHr) {
        await setupNativeMobileHr(config);
      }

      try {
        const authData = await authorize(config, appKind, {
          useOrgFallback: this.#config.useOrgFallback ?? true,
          isNativeMobileHr,
        });
        const initData = await this.init(authData, authData.user);
        return {
          ...authData,
          ...initData,
        };
      } catch (error) {
        if (error instanceof TellentClientAuthError) {
          const result = {
            ...(handleAuthError(error, config) || {}),
            appKind,
          };

          if (result.continueOnError) {
            const initData = await this.init(result, result.user);

            return {
              ...result,
              ...initData,
            };
          }
        }

        throw error;
      }
    });
  }

  public async init(
    credentials: Partial<TellentClientInitData>,
    user?: TellentCurrentUser,
  ): Promise<TellentClientBootstrapData> {
    const { appRef } = await this.#injector.get(TellentClientBootstrap).createApplication(
      [
        ...this.#bootstrapProviders,
        ...resolveErrorHandlerProviders(this.#config, credentials.appGuid),
        ...resolveAppProviders(this.#config),
        { provide: TELLENT_CLIENT_CONFIG, useValue: Object.freeze({ ...this.#config }) },
        provideRouter(ROUTES, withDisabledInitialNavigation()),
        {
          provide: TELLENT_ADOPTED_STYLESHEETS,
          useValue: this.#dynamicConfig.customStylesheets.asReadonly(),
        },
        provideDynamicAnalyticsClient({
          useValue: this.#dynamicConfig.analyticsCallbacks,
        }),
        provideTellentClientInternalRoute({
          useFactory: currentUserProfileTellentClientRoute,
        }),
      ],
      this.#config,
      credentials,
      user,
    );

    // Resolved together: the first subscription dispatches the one `/features` fetch and the rest
    // join it, so this costs no more requests than awaiting them one by one did.
    const [oneSettings, oneDomain, gettingStarted, onePositionsHistory] =
      await runInInjectionContext(appRef.injector, () =>
        Promise.all([
          injectDevFlagPromise(TellentDevFlag.OneSettings),
          injectDevFlagPromise(TellentDevFlag.OneDomain),
          injectDevFlagPromise(TellentDevFlag.GettingStartedTac),
          injectDevFlagPromise(TellentDevFlag.OnePositionsHistory),
        ]),
      );

    const isNativeMobileHr = runInInjectionContext(appRef.injector, () => injectIsNativeMobileHr());

    runInInjectionContext(appRef.injector, () =>
      this.#config.env.includes('local') || isNativeMobileHr || !this.#config.oneDomainRedirect
        ? null
        : redirectToOneDomainIfLegacyDomainVisited(
            oneDomain,
            inject<TellentAdminAppConfig>(APP_CONFIG).tellentOneDomainUrl,
          ),
    );

    appRef.injector.get(TellentClientGlobalNotificationsService);

    if (isNativeMobileHr) {
      appRef.injector.get(MobilePushDeviceRegistrationService);
    }

    defineCustomElements(TellentClientComponents, appRef.injector);

    await appRef.injector.get(ApplicationInitStatus).donePromise;

    this.internalRoutes = runInInjectionContext(appRef.injector, () =>
      injectTellentClientInternalRoutes(),
    );

    this.#ready$.next(appRef);
    this.#ready$.complete();

    const orgGuid = appRef.injector.get(ORG_GUID);
    const member = appRef.injector
      .get(TELLENT_INITIAL_CURRENT_USER)
      .members.find(member => member.orgGuid === orgGuid)!;

    const version = appRef.injector.get(AppRevisionToken);
    appRef.injector.get(I18nService).localeChanged.subscribe(locale => moment.locale(locale));

    // eslint-disable-next-line no-console
    console.log(
      `[TellentClient] ${version}\norgGuid: "${orgGuid}"\nappGuid: "${credentials.appGuid}"\nmemberGuid: "${member?.guid}"`,
    );

    return {
      member,
      features: { oneSettings, oneDomain, gettingStarted, onePositionsHistory },
    };
  }

  public getTellentUrl(appKind: `${AppKind}`, page: string, params: object = {}, orgGuid?: string) {
    return this.ready.then(appRef =>
      runInInjectionContext(appRef.injector, () => {
        const urlBuilder = injectTellentAppUrlBuilder();
        const _orgGuid = orgGuid ?? inject(ORG_GUID);

        const routeConfig: any = params
          ? {
              id: page,
              params,
            }
          : { id: page };

        return urlBuilder(appKind, routeConfig, _orgGuid!);
      }),
    );
  }

  public reload() {
    this.ready.then(appRef =>
      runInInjectionContext(appRef.injector, () => {
        const urlBuilder = injectTellentAppUrlBuilder();
        const appKind = injectTellentAppKind();
        const orgGuid = inject(ORG_GUID);

        window.open(urlBuilder(appKind, undefined, orgGuid!), '_self');
      }),
    );
  }

  public get navigation() {
    return this.ready.then(appRef => {
      const routingSvc = appRef.injector.get(TellentNavigationRoutingService);
      const mainNavSvc = appRef.injector.get(TellentMainNavigationStateService);
      const dataFacade = appRef.injector.get(TellentNavigationDataFacade);

      return {
        refresh: () => dataFacade.refreshMainNav(),
        registerLinkHandler: (handler: (url: string) => void) =>
          routingSvc.registerNavHandler(handler),
        location: routingSvc.location,
        /**
         * @deprecated use "hostAppRoutes" instead
         */
        recognizedRoutes: routingSvc.hostAppRoutes,
        hostAppRoutes: routingSvc.hostAppRoutes,
        minimized: mainNavSvc.minimized,
        forceMaximized: mainNavSvc.forceMaximized,
      };
    });
  }

  public get walkthrough() {
    return this.ready.then(appRef => appRef.injector.get(TellentGettingStartedFacade));
  }

  // TODO: refactor https://github.com/Recruitee/recruitee/pull/18767#discussion_r3014246608 revisit when the reports & TC bootstrapping gets reworked.
  // Exposed here so host apps (e.g. HR) can fetch analytics feature flags before
  // the reports shell initializes — flags must be available prior to all other reports requests
  public get reports(): Promise<ReportsFacade> {
    return this.ready.then(appRef => new ReportsFacade(appRef.injector));
  }

  public setSharedStylesheets(stylesheets: CSSStyleSheet[]) {
    this.#dynamicConfig.customStylesheets.set(stylesheets);
  }

  public setAnalyticsClient(client: TellentClientConfig['analyticsCallbacks'] | null) {
    this.#dynamicConfig.analyticsCallbacks.set(client);
  }

  private validateConfig(config: TellentClientConfig): TellentClientConfig {
    const defaults: Partial<TellentClientConfig> = {
      oneDomainRedirect: true,
    };

    const envs = Object.keys(environments);

    if (!config.env || !envs.includes(config.env)) {
      throw new Error(
        `Missing required property:\n"config.env": ${envs.map(e => `"${e}"`).join(' | ')}`,
      );
    }

    return {
      ...defaults,
      ...config,
    };
  }
}
